// CPU-simulated particles in one SpritePool (sparks, shatter pixels, pixel-sort bars, paper leaflets, INVOICE
// columns). Allocation is a ring over the pool, so when it is full the oldest particle is recycled. Spawning is
// capped per frame; a burst beyond the remaining budget is cut short, never queued.

import { Vector3, type Color } from 'three'
import { InvariantViolation } from '@taxwar/net'
import type { GlyphAtlas } from './atlas'
import { effectsOf, type ParticleDef } from './effects'
import { tangentAt } from './paths'
import type { PointPlacement } from './placement'
import { SpritePool } from './SpritePool'
import { scaledCount } from './slots'
import { FLICKER_HZ, TAU } from './timing'
import type { VfxUniforms } from './uniforms'

/** Live particles at High quality. */
const PARTICLE_CAPACITY_HIGH = 2048
/** Particles spawned per frame at High quality. */
const SPAWN_BUDGET_HIGH = 384
/** Fractions of a particle's life spent fading in and out. */
const FADE_IN = 0.1
const FADE_OUT = 0.4
/** Paper rocking rate (radians of phase per second). */
const FLUTTER_RAD_PER_SEC = 5
/** Column glyphs sit this many character heights apart. */
const COLUMN_LINE_SPACING = 0.9
const XYZ = 3
const NO_DEF = -1

export class ParticleSystem {
  readonly pool: SpritePool
  private readonly capacity: number
  private readonly spawnBudget: number
  private budgetLeft: number
  private cursor = 0

  /** Every particle definition in the registry; particles store an index into it. */
  private readonly defs: readonly ParticleDef[]
  private readonly defIndex = new Map<ParticleDef, number>()
  /** Per particle: its def index (NO_DEF = dead), kinematics and timing. */
  private readonly kinds: Int16Array
  private readonly positions: Float32Array
  private readonly velocities: Float32Array
  private readonly ages: Float32Array
  private readonly lives: Float32Array
  private readonly phases: Float32Array

  // Spawn scratch.
  private readonly tangent = new Vector3()
  private readonly bitangent = new Vector3()
  private readonly direction = new Vector3()
  private readonly spawnAt = new Vector3()

  constructor(
    private readonly atlas: GlyphAtlas,
    private readonly uniforms: VfxUniforms,
    private readonly scale: number,
  ) {
    const defs: ParticleDef[] = []
    for (const [, def] of effectsOf('burst')) defs.push(def.particle)
    for (const [, def] of effectsOf('rain')) defs.push(def.particle)
    this.defs = defs
    defs.forEach((def, index) => this.defIndex.set(def, index))
    this.capacity = scaledCount(PARTICLE_CAPACITY_HIGH, scale)
    this.spawnBudget = scaledCount(SPAWN_BUDGET_HIGH, scale)
    this.budgetLeft = this.spawnBudget
    this.pool = new SpritePool(this.capacity, atlas.texture, uniforms)
    this.kinds = new Int16Array(this.capacity).fill(NO_DEF)
    this.positions = new Float32Array(this.capacity * XYZ)
    this.velocities = new Float32Array(this.capacity * XYZ)
    this.ages = new Float32Array(this.capacity)
    this.lives = new Float32Array(this.capacity)
    this.phases = new Float32Array(this.capacity)
  }

  /** A burst of `highCount` particles (or columns) at High quality, scaled to the tier and capped by the budget. */
  burst(def: ParticleDef, highCount: number, placement: PointPlacement, colour: Color): void {
    const count = scaledCount(highCount, this.scale)
    for (let i = 0; i < count; i++) {
      if (!this.spawnUnit(def, placement, colour)) return
    }
  }

  /**
   * One particle, or one column of glyph particles moving together; false when this frame's spawn budget cannot
   * hold it.
   */
  spawnUnit(def: ParticleDef, placement: PointPlacement, colour: Color): boolean {
    const shape = def.shape
    const sprites = shape.kind === 'column' ? shape.text.length : 1
    if (sprites > this.budgetLeft) return false
    this.budgetLeft -= sprites
    const kind = this.defIndex.get(def)
    if (kind === undefined) throw new InvariantViolation('particle definition is not in the VFX registry')

    // Shared motion for the unit: a random point in the spawn disc, moving outward and up.
    const normal = placement.at
    tangentAt(normal, this.tangent)
    this.bitangent.crossVectors(normal, this.tangent)
    const angle = Math.random() * TAU
    const radius = def.spread * placement.extent * Math.sqrt(Math.random())
    this.direction.copy(this.tangent).multiplyScalar(Math.cos(angle)).addScaledVector(this.bitangent, Math.sin(angle))
    this.spawnAt
      .copy(normal)
      .addScaledVector(this.direction, radius)
      .normalize()
      .multiplyScalar(placement.altitude + def.height)
    const speed = between(def.speed)
    const lift = between(def.lift)
    const life = between(def.life)
    const delay = between(def.delay)
    const phase = Math.random() * TAU
    const width = def.size[0]
    const height = def.size[1]
    const inverseRadius = 1 / this.spawnAt.length()

    for (let s = 0; s < sprites; s++) {
      const slot = this.cursor
      this.cursor = (this.cursor + 1) % this.capacity
      const cell = shape.kind === 'column' ? this.atlas.cellOfChar(shape.text.charCodeAt(s)) : this.atlas.cellOfShape(shape.cell)
      const offsetY = shape.kind === 'column' ? -s * height * COLUMN_LINE_SPACING : 0
      const o = slot * XYZ
      this.kinds[slot] = kind
      this.positions[o] = this.spawnAt.x
      this.positions[o + 1] = this.spawnAt.y
      this.positions[o + 2] = this.spawnAt.z
      this.velocities[o] = this.direction.x * speed + this.spawnAt.x * inverseRadius * lift
      this.velocities[o + 1] = this.direction.y * speed + this.spawnAt.y * inverseRadius * lift
      this.velocities[o + 2] = this.direction.z * speed + this.spawnAt.z * inverseRadius * lift
      this.ages[slot] = -delay
      this.lives[slot] = life
      this.phases[slot] = phase
      this.pool.setLook(slot, colour, def.intensity, cell, width, height, 0, offsetY, def.jitter, Math.random())
      this.pool.hide(slot)
    }
    return true
  }

  /** Opens a new spawn window: everything spawned until the next call shares one frame's budget. */
  beginFrame(): void {
    this.budgetLeft = this.spawnBudget
  }

  update(dtSeconds: number): void {
    const motion = this.uniforms.uMotion.value
    for (let slot = 0; slot < this.capacity; slot++) {
      const kind = this.kinds[slot] as number
      if (kind === NO_DEF) continue
      const def = this.defs[kind] as ParticleDef
      const age = (this.ages[slot] as number) + dtSeconds
      this.ages[slot] = age
      if (age < 0) continue
      const life = this.lives[slot] as number
      if (age >= life) {
        this.kinds[slot] = NO_DEF
        this.pool.hide(slot)
        continue
      }
      const o = slot * XYZ
      let x = this.positions[o] as number
      let y = this.positions[o + 1] as number
      let z = this.positions[o + 2] as number
      let vx = this.velocities[o] as number
      let vy = this.velocities[o + 1] as number
      let vz = this.velocities[o + 2] as number
      // Gravity pulls toward the globe centre; drag damps.
      const pull = (def.gravity * dtSeconds) / Math.sqrt(x * x + y * y + z * z)
      const damping = Math.max(0, 1 - def.drag * dtSeconds)
      vx = (vx - x * pull) * damping
      vy = (vy - y * pull) * damping
      vz = (vz - z * pull) * damping
      x += vx * dtSeconds
      y += vy * dtSeconds
      z += vz * dtSeconds
      this.positions[o] = x
      this.positions[o + 1] = y
      this.positions[o + 2] = z
      this.velocities[o] = vx
      this.velocities[o + 1] = vy
      this.velocities[o + 2] = vz

      const t = age / life
      const phase = this.phases[slot] as number
      let alpha = Math.min(1, t / FADE_IN) * Math.min(1, (1 - t) / FADE_OUT)
      alpha *= 1 - def.flicker * motion * (0.5 + 0.5 * Math.sin(TAU * FLICKER_HZ * age + phase))
      const scale = def.scale[0] + (def.scale[1] - def.scale[0]) * t
      const rotation = def.flutter * Math.sin(FLUTTER_RAD_PER_SEC * age + phase)
      this.pool.setMotion(slot, x, y, z, alpha, scale, rotation)
    }
    this.pool.upload()
  }

  dispose(): void {
    this.pool.dispose()
  }
}

function between(range: readonly [number, number]): number {
  return range[0] + (range[1] - range[0]) * Math.random()
}
